/*
 * saif-cart-guard.js — a 1-on-1 session must never be paid for unscheduled.
 *
 * WHY THIS EXISTS
 * Booking sessions are created by the orders/paid webhook, which reads the
 * `_booking_data` line-item property written by the booking wizard. A service
 * variant that reaches checkout WITHOUT that property is money taken for
 * nothing: the webhook takes its `orphanServiceItems` branch, creates zero
 * bookings and only fires a manual-review alert. That is exactly what happened
 * to order #1028 — the customer paid for two lessons and held none.
 *
 * The product page no longer offers a direct Add to cart, so the obvious route
 * is closed. This is the backstop for every other one: an old cart restored on
 * a return visit, a stale link, a variant id posted straight to /cart/add.js,
 * or any future template that forgets the wizard.
 *
 * WHAT IT DOES
 * On the cart page only: if a service variant is present with no
 * `_booking_data`, remove that line and explain why, with a button into the
 * wizard. Removing rather than merely blocking checkout avoids a dead end —
 * the wizard adds a fresh line with the booking data attached, and there is no
 * orphan left behind to confuse the total.
 *
 * Lines that DO carry `_booking_data` are never touched.
 */
(function () {
  'use strict';

  /* Mirrors SERVICE_CATALOG in the worker. If a service variant is added there
     it must be added here too, or the guard silently stops covering it. */
  var SERVICE_VARIANTS = {
    '52723662422335': '1-on-1 Lesson',
    '53992218526015': '5-Lesson Package'
  };
  var BOOKING_URL = '/pages/book-a-session';

  function onCartPage() {
    return /^\/cart(\/|$)/.test(window.location.pathname);
  }

  function isOrphan(item) {
    if (!SERVICE_VARIANTS[String(item.variant_id)]) return false;
    var p = item.properties || {};
    var raw = p._booking_data;
    if (!raw) return true;
    // Present but unusable is still unscheduled — the webhook would fail to
    // parse it and create nothing.
    try {
      var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return !(parsed && Array.isArray(parsed.sessions) && parsed.sessions.length);
    } catch (e) {
      return true;
    }
  }

  function notice(names) {
    if (document.getElementById('SaifCartGuardNotice')) return;
    var host = document.querySelector('.cart__footer, cart-items, main, body');
    if (!host) return;
    var one = names.length === 1;
    var el = document.createElement('div');
    el.id = 'SaifCartGuardNotice';
    el.setAttribute('role', 'status');
    el.style.cssText = [
      'margin:1.2rem 0', 'padding:1.4rem 1.6rem', 'border:1px solid rgba(184,134,11,0.4)',
      'border-radius:12px', 'background:rgba(184,134,11,0.08)',
      'font-family:inherit', 'line-height:1.5', 'max-width:60rem'
    ].join(';');

    var h = document.createElement('p');
    h.style.cssText = 'margin:0 0 .5rem;font-weight:700;color:#1A1814';
    h.textContent = one ? 'Your session still needs a time' : 'Your sessions still need times';
    el.appendChild(h);

    var p = document.createElement('p');
    p.style.cssText = 'margin:0 0 1rem;color:#4A443E';
    p.textContent = (one ? 'A ' + names[0] + ' was' : 'These were') +
      ' removed from your cart because no date and time had been chosen. ' +
      'Pick a slot and it will be added back, ready to check out.';
    el.appendChild(p);

    var a = document.createElement('a');
    a.href = BOOKING_URL;
    a.textContent = 'Choose your time →';
    a.style.cssText = [
      'display:inline-block', 'padding:1rem 1.6rem', 'border-radius:999px',
      'background:#B8860B', 'color:#fff', 'font-weight:700', 'text-decoration:none'
    ].join(';');
    el.appendChild(a);

    host.insertBefore(el, host.firstChild);
  }

  function run() {
    if (!onCartPage()) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var orphans = (cart.items || []).filter(isOrphan);
        if (!orphans.length) return null;
        var names = orphans.map(function (i) {
          return SERVICE_VARIANTS[String(i.variant_id)] || i.title;
        });
        // Remove by line index, highest first: /cart/change.js re-indexes after
        // each call, so removing low-to-high would shift the later targets.
        var lines = orphans
          .map(function (i) { return (cart.items || []).indexOf(i) + 1; })
          .sort(function (a, b) { return b - a; });
        var chain = Promise.resolve();
        lines.forEach(function (line) {
          chain = chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: line, quantity: 0 })
            });
          });
        });
        return chain.then(function () {
          try {
            console.warn('[saif-cart-guard] removed unscheduled service line(s):', names.join(', '));
            // Carry the real names across the reload so the message names what
            // was actually removed rather than guessing.
            sessionStorage.setItem('saif:cartguard:removed', JSON.stringify(names));
          } catch (e) {}
          // Repaint the cart so the totals match what we just did; the notice
          // is drawn on the way back in, not here, since this discards the DOM.
          window.location.replace('/cart?scheduled=needed');
        });
      })
      .catch(function (err) {
        // Never block a working cart because this check failed.
        try { console.warn('[saif-cart-guard] skipped:', err); } catch (e) {}
      });
  }

  // Show the explanation after the reload that follows a removal.
  function afterReload() {
    if (!onCartPage()) return;
    if (window.location.search.indexOf('scheduled=needed') === -1) return;
    var names = ['session'];
    try {
      var raw = sessionStorage.getItem('saif:cartguard:removed');
      if (raw) names = JSON.parse(raw) || names;
      sessionStorage.removeItem('saif:cartguard:removed');   // one showing only
    } catch (e) {}
    notice(names);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { afterReload(); run(); });
  } else {
    afterReload();
    run();
  }
})();
